import { useTheme } from '@emotion/react'
import { alpha } from '@mui/system';
import Table from '@mui/material/Table';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableFooter from '@mui/material/TableFooter';
import TableRow from '@mui/material/TableRow';
import usePreferences from '@components/_hooks/usePreferences';

const defaultBodySx = {
    '& .MuiTableCell-root': {
        pt: 0.5,
        pb: 0.5,
        pr: 1.8,
        pl: 1.8,
        border: 'none',
        fontSize: {
            md: '12px',
            xs: '7px'
        }
    }
}

const PrimaryTable = (props) => {
    const theme = useTheme();
    const { quoteTable } = usePreferences();
    const quoteFontFamily = quoteTable.quoteFontFamily();
    const {
        children,
        sx,
        color = 'primary',
        border = `1px solid ${alpha(theme.palette[props?.color ? props.color : 'primary'].main, 0.2)}`,
        ...rest
    } = props;


    return (
        <Table
            size="small"
            sx={{
                border: border,
                ...(quoteFontFamily == 'roboto' && {
                    '& .MuiTableCell-root': {
                        fontFamily: "var(--font-roboto)"
                    },
                    '& .MuiTypography-root': {
                        fontFamily: "var(--font-roboto)"
                    },
                }),
                ...sx
            }}
            {...rest}
        >
            {children}
        </Table>
    )
}


PrimaryTable.Head = ({ children, sx, ...rest }) => <TableHead {...rest} sx={{ ...sx }}>{children}</TableHead>
PrimaryTable.Body = ({ children, sx, ...rest }) => <TableBody {...rest} sx={{ ...defaultBodySx, ...sx }}>{children}</TableBody>
PrimaryTable.Footer = ({ children, sx, ...rest }) => <TableFooter {...rest} sx={{ ...defaultBodySx, ...sx }}>{children}</TableFooter>

PrimaryTable.Row = ({ children, sx, last = true, override = false, swapEven = false, opacity = false, lastOpacity = 0.8, color = 'primary', evenOpacity = 0.1, ...rest }) => {
    const theme = useTheme();
    const setOpacity = opacity !== false ? opacity : lastOpacity;
    const rowSx = {
        ...(last && {
            '&:last-child td, &:last-child th': {
                backgroundColor: alpha(theme.palette[color].main, setOpacity),
                // color: lastOpacity > 0.5 ? theme.palette.primary.contrastText : theme.palette.offWhite.main,
                color: setOpacity > 0.3 ? theme.palette[color].contrastText : 'inherit'
            },
        }),
        ...(!swapEven ? {
            '&:nth-of-type(even)': {
                backgroundColor: alpha(theme.palette[color].main, evenOpacity),
            },
        } : {
            '&:nth-of-type(odd)': {
                backgroundColor: alpha(theme.palette[color].main, evenOpacity),
            },
        }),
        ...(override != false && {
            '& td, & th': {
                backgroundColor: alpha(theme.palette[color].main, override),
                color: override > 0.3 ? theme.palette[color].contrastText : 'inherit'
            },
        }),
    }

    return (
        <TableRow
            sx={{
                ...rowSx,
                ...sx,
            }}
            {...rest}
        >
            {children}
        </TableRow>
    )
}
PrimaryTable.Title = ({ children, override = 1, rowSx, last = false, rowProps, sx, className, ...rest }) => (
    <PrimaryTable.Row {...rowProps} override={override} last={last} sx={{ ...defaultBodySx, ...rowSx }}>
        <TableCell className={`text-center fw-bold ${className}`} {...rest}>
            {children}
        </TableCell>
    </PrimaryTable.Row>
)

PrimaryTable.Cell = ({ children, sx, ...rest }) => (
    <TableCell {...rest} sx={{ ...sx }}>{children}</TableCell>
)

export function renderHeaders({ headers, data, lastOpacity = 0.8, withoutColon = true }) {
    return headers && data && headers.map((header) => (
        <PrimaryTable.Row key={header.key} lastOpacity={lastOpacity}>
            <PrimaryTable.Cell>{header.label}{(header?.withoutColon || withoutColon) ? '' : ':'}</PrimaryTable.Cell>
            <PrimaryTable.Cell>
                {header.custom
                    ? header.custom(data[header.key])
                    : data[header.key]
                }
            </PrimaryTable.Cell>
        </PrimaryTable.Row>
    ))
}

export function renderRows({ rows, data, lastOpacity = 0.8 }) {
    return rows && data && <PrimaryTable.Row lastOpacity={lastOpacity}>
        {rows.map((row, idx) => (
            <PrimaryTable.Cell key={idx}>
                {row.custom
                    ? row.custom(data[row.key], data)
                    : data[row.key]
                }
            </PrimaryTable.Cell>
        ))}
    </PrimaryTable.Row>
}

export default PrimaryTable